/**
 * geodesic.ts
 *
 * The ONLY frontend entry point for geodesic (ellipsoidal WGS84) distance —
 * the canonical `geodesic_wgs84` chainage convention (SEMANTICS.md). Wraps
 * GeographicLib's own JS port, which agrees with the backend's pyproj.Geod to
 * nanometres; the shared proj-truth fixtures pin both sides.
 *
 * Mercator/screen math stays legal for rendering and hit testing, never for a
 * reported or persisted distance. Karney's inverse costs microseconds per
 * segment — fine for authoring and measurement, keep it out of per-frame
 * render loops.
 */

import { Geodesic } from "geographiclib-geodesic";

const WGS84_GEODESIC = Geodesic.WGS84;

export interface LatLongPoint {
  latitude: number;
  longitude: number;
}

/** Geodesic plan distance in metres between two WGS84 positions. */
export function geodesicDistance(a: LatLongPoint, b: LatLongPoint): number {
  const result = WGS84_GEODESIC.Inverse(a.latitude, a.longitude, b.latitude, b.longitude);
  return result.s12 ?? 0;
}

/**
 * Cumulative geodesic plan distances at each vertex, starting at 0. Elevation
 * on the vertices is ignored: chainage is horizontal plan distance.
 */
export function geodesicArcLengths(vertices: readonly LatLongPoint[]): number[] {
  const cumulative: number[] = [];
  let total = 0;
  for (let index = 0; index < vertices.length; index += 1) {
    if (index > 0) {
      total += geodesicDistance(vertices[index - 1], vertices[index]);
    }
    cumulative.push(total);
  }
  return cumulative;
}

/** Total geodesic plan length in metres of a WGS84 polyline. */
export function geodesicLength(vertices: readonly LatLongPoint[]): number {
  const arcLengths = geodesicArcLengths(vertices);
  return arcLengths.length > 0 ? arcLengths[arcLengths.length - 1] : 0;
}

/**
 * The WGS84 position `distance` metres along the geodesic from `a` toward
 * `b`. Distances beyond the segment extrapolate along the same geodesic.
 */
export function geodesicInterpolate(
  a: LatLongPoint,
  b: LatLongPoint,
  distance: number
): LatLongPoint {
  const inverse = WGS84_GEODESIC.Inverse(a.latitude, a.longitude, b.latitude, b.longitude);
  const direct = WGS84_GEODESIC.Direct(a.latitude, a.longitude, inverse.azi1 ?? 0, distance);
  return { latitude: direct.lat2 ?? a.latitude, longitude: direct.lon2 ?? a.longitude };
}
